import React, { JSX } from "react";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/outline";
import { useStore } from "../../store";

export const WalkthroughPanel: React.FC = () => {
  const currentId = useStore((s) => s.currentId);
  const stepIndex = useStore((s) => s.stepIndex);
  const status = useStore((s) => s.status);
  const nextStep = useStore((s) => s.nextStep);
  const prevStep = useStore((s) => s.prevStep);
  const stopWalkthrough = useStore((s) => s.stopWalkthrough);
  const steps = useStore((s) => s.steps);
  const currentStep =
    steps && steps.length > 0
      ? steps[Math.max(0, Math.min(stepIndex, steps.length - 1))]
      : null;

  if (status === "idle" || !currentId) return null;

  const isFirstStep = stepIndex === 0;
  const isSecondStep = stepIndex === 1;

  // Local mapping of control names to keyboard hints
  const controlKeyHints: Record<string, string[]> = {
    slew: ["ArrowLeft", "ArrowRight"],
    boom: ["ArrowUp", "ArrowDown"],
    hoist: ["W", "S"],
    speed: ["W", "S"],
    alerts: ["[", "]"],
  };

  const renderKeyBadge = (label: string) => {
    const iconMap: Record<string, JSX.Element> = {
      ArrowLeft: <ArrowLeftIcon className="w-4 h-4" />,
      ArrowRight: <ArrowRightIcon className="w-4 h-4" />,
      ArrowUp: <ArrowUpIcon className="w-4 h-4" />,
      ArrowDown: <ArrowDownIcon className="w-4 h-4" />,
    };
    // Support combinations like "Shift+ArrowUp"
    if (label.includes("+")) {
      const parts = label.split("+");
      const children: (JSX.Element | string)[] = [];
      parts.forEach((part, idx) => {
        if (idx > 0)
          children.push(
            <span key={`plus-${idx}`} className="mx-1">
              +
            </span>
          );
        const icon = iconMap[part];
        children.push(
          <span
            key={`part-${part}-${idx}`}
            className="inline-flex items-center"
          >
            {icon || part}
          </span>
        );
      });
      return (
        <span
          key={label}
          className="inline-flex items-center justify-center rounded border border-gray-300 bg-gray-50 text-gray-700 text-xs px-2 py-1 mr-1 mb-1"
          title={label}
        >
          {children}
        </span>
      );
    }
    const content = iconMap[label] || label;
    return (
      <span
        key={label}
        className="inline-flex items-center justify-center rounded border border-gray-300 bg-gray-50 text-gray-700 text-xs px-2 py-1 mr-1 mb-1"
        title={label}
      >
        {content}
      </span>
    );
  };

  const renderControlHints = (controls?: string[]) => {
    if (!controls || controls.length === 0) return null;
    const keys = controls.flatMap((c) => controlKeyHints[c] || []);
    if (keys.length === 0) return null;
    return (
      <div className="mt-2">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
          Keyboard
        </div>
        <div className="flex flex-wrap">{keys.map(renderKeyBadge)}</div>
      </div>
    );
  };

  return (
    <>
      {/* Backdrop only for the first step */}
      <div
        className={
          `fixed inset-0 z-30 transition-opacity duration-300 ` +
          (isFirstStep ? "opacity-100" : "opacity-0 pointer-events-none")
        }
      >
        <div className="absolute inset-0 bg-black/50" />
      </div>

      {/* Single card that animates from center to bottom-right */}
      <div
        className={
          `fixed bottom-4 right-4 z-40 w-96 rounded-md border border-gray-300 bg-white shadow-lg p-4 space-y-3 ` +
          `transition-all duration-500 ease-in-out ` +
          (isFirstStep
            ? "right-[50%] translate-x-[50%] translate-y-[calc(-50vh+1rem+50%)]"
            : isSecondStep
            ? "right-[50%] translate-x-[50%] translate-y-0"
            : "right-[2%] translate-x-[0%] translate-y-0")
        }
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Walkthrough</h3>
          {stepIndex > 0 && (
            <button
              onClick={stopWalkthrough}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          )}
        </div>
        {currentStep && (
          <div className="text-sm text-gray-700">
            <div className="font-medium mb-1">{currentStep.title}</div>
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{
                __html: (currentStep as any).bodyHtml,
              }}
            />
            {renderControlHints((currentStep as any).controls)}
          </div>
        )}
        <div className="flex items-center justify-between">
          {stepIndex > 0 ? (
            <button
              onClick={prevStep}
              className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Prev
            </button>
          ) : (
            <span />
          )}
          {steps && stepIndex < steps?.length - 1 ? (
            <button
              onClick={nextStep}
              className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Next
            </button>
          ) : (
            <button
              onClick={stopWalkthrough}
              className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Finished
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default WalkthroughPanel;
